import { useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import PriorityOrderField from "@/components/PriorityOrderField";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";

// Editor for assignment_rules. Captures compatibility + eligibility.

const PROFICIENCY = ["beginner", "intermediate", "advanced", "expert"];
const PROFICIENCY_LABEL = {
  beginner: "Beginner", intermediate: "Intermediate",
  advanced: "Advanced", expert: "Expert",
};

const EMPTY = {
  category_id: "",
  name: "",
  description: "",
  priority: 100,
  is_active: true,
  combine_mode: "any",
  combine_category_ids: [],
  max_concurrent_for_employee: null,
  required_skills: [],
  preferred_skills: [],
};

export default function RuleEditorDialog({ open, onOpenChange, editing, categories, skills, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      if (editing) {
        setForm({
          category_id: editing.category_id || "",
          name: editing.name || "",
          description: editing.description || "",
          priority: editing.priority ?? 100,
          is_active: editing.is_active !== false,
          combine_mode: editing.combine_mode || "any",
          combine_category_ids: editing.combine_category_ids || [],
          max_concurrent_for_employee: editing.max_concurrent_for_employee ?? null,
          required_skills: (editing.required_skills || []).map((r) => ({
            skill_id: r.skill_id, min_proficiency: r.min_proficiency || "beginner",
            require_valid_cert: !!r.require_valid_cert,
          })),
          preferred_skills: (editing.preferred_skills || []).map((r) => ({
            skill_id: r.skill_id, weight: r.weight ?? 1,
          })),
        });
      } else {
        setForm(EMPTY);
      }
    }
  }, [open, editing]);

  const set = (patch) => setForm((p) => ({ ...p, ...patch }));

  const toggleCombineCat = (cid) => {
    const list = new Set(form.combine_category_ids || []);
    if (list.has(cid)) list.delete(cid);
    else list.add(cid);
    set({ combine_category_ids: Array.from(list) });
  };

  const addRequired = () =>
    set({ required_skills: [...form.required_skills,
      { skill_id: "", min_proficiency: "beginner", require_valid_cert: false }] });
  const removeRequired = (i) =>
    set({ required_skills: form.required_skills.filter((_, idx) => idx !== i) });
  const updateRequired = (i, patch) =>
    set({ required_skills: form.required_skills.map((r, idx) =>
      idx === i ? { ...r, ...patch } : r) });

  const addPreferred = () =>
    set({ preferred_skills: [...form.preferred_skills, { skill_id: "", weight: 1 }] });
  const removePreferred = (i) =>
    set({ preferred_skills: form.preferred_skills.filter((_, idx) => idx !== i) });
  const updatePreferred = (i, patch) =>
    set({ preferred_skills: form.preferred_skills.map((r, idx) =>
      idx === i ? { ...r, ...patch } : r) });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    if (!form.category_id) { toast.error("Category is required"); return; }
    if (form.required_skills.some((r) => !r.skill_id)) {
      toast.error("Pick a skill for each required row"); return;
    }
    if (form.preferred_skills.some((r) => !r.skill_id)) {
      toast.error("Pick a skill for each preferred row"); return;
    }
    setBusy(true);
    try {
      const body = {
        category_id: form.category_id,
        name: form.name.trim(),
        description: form.description || null,
        priority: Number(form.priority) || 100,
        is_active: form.is_active,
        combine_mode: form.combine_mode,
        combine_category_ids: form.combine_mode === "any" ? [] : form.combine_category_ids,
        max_concurrent_for_employee: form.max_concurrent_for_employee ?? null,
        required_skills: form.required_skills,
        preferred_skills: form.preferred_skills,
      };
      if (editing) {
        await api.patch(`/assignment-rules/${editing.rule_id}`, body);
        toast.success("Rule updated");
      } else {
        await api.post("/assignment-rules", body);
        toast.success("Rule created");
      }
      onSaved();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const activeCategories = categories.filter((c) => c.is_active !== false);
  const otherCategories = activeCategories.filter((c) => c.category_id !== form.category_id);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" fullscreenOnMobile data-testid="rule-editor-dialog">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit assignment rule" : "New assignment rule"}</DialogTitle>
          <DialogDescription>
            Define which employees can be assigned to which categories. Use these to
            ensure the right people are scheduled to the right work, with the right
            skills and combinations.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit}>
          <Tabs defaultValue="basics">
            <TabsList className="bg-[#F6F9FE] border border-slate-200 h-9 p-1 mb-4" data-testid="rule-tabs">
              <TabsTrigger value="basics"        data-testid="rule-tab-basics"
                className="data-[state=active]:bg-white data-[state=active]:text-[#1D4ED8] rounded-md px-4">Basics</TabsTrigger>
              <TabsTrigger value="combinations"  data-testid="rule-tab-combinations"
                className="data-[state=active]:bg-white data-[state=active]:text-[#1D4ED8] rounded-md px-4">Combinations</TabsTrigger>
              <TabsTrigger value="eligibility"   data-testid="rule-tab-eligibility"
                className="data-[state=active]:bg-white data-[state=active]:text-[#1D4ED8] rounded-md px-4">Eligibility</TabsTrigger>
            </TabsList>

            <TabsContent value="basics">
              <div className="space-y-4">
                <div>
                  <Label className="eyebrow">Name</Label>
                  <Input required value={form.name} data-testid="rule-name"
                    onChange={(e) => set({ name: e.target.value })}
                    className="h-11 rounded-lg mt-2"
                    placeholder="e.g. Inbound must combine with QA" />
                </div>
                <div>
                  <Label className="eyebrow">Category</Label>
                  <Select value={form.category_id}
                    onValueChange={(v) => set({ category_id: v })}>
                    <SelectTrigger className="h-11 rounded-lg mt-2"
                      data-testid="rule-category"><SelectValue placeholder="Choose a category" /></SelectTrigger>
                    <SelectContent>
                      {activeCategories.map((c) => (
                        <SelectItem key={c.category_id} value={c.category_id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <PriorityOrderField
                      value={form.priority}
                      onChange={(v) => set({ priority: v })}
                      testid="rule-priority" />
                  </div>
                  <div>
                    <Label className="eyebrow">Status</Label>
                    <Select value={form.is_active ? "active" : "archived"}
                      onValueChange={(v) => set({ is_active: v === "active" })}>
                      <SelectTrigger className="h-11 rounded-lg mt-2"
                        data-testid="rule-status"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="archived">Archived</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label className="eyebrow">Description (optional)</Label>
                  <Input value={form.description} data-testid="rule-description"
                    onChange={(e) => set({ description: e.target.value })}
                    className="h-11 rounded-lg mt-2"
                    placeholder="When and why this rule applies" />
                </div>
              </div>
            </TabsContent>

            <TabsContent value="combinations">
              <p className="text-xs text-[#4B5773] mb-3">
                Control which types of work can be done together on the same shift, for example,
                allow Inbound + QA but block Inbound + Outbound.
              </p>
              <div className="space-y-4">
                <div>
                  <Label className="eyebrow">Combine mode</Label>
                  <Select value={form.combine_mode}
                    onValueChange={(v) => set({ combine_mode: v })}>
                    <SelectTrigger className="h-11 rounded-lg mt-2"
                      data-testid="rule-combine-mode"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="any">No restriction (can combine with anything)</SelectItem>
                      <SelectItem value="allowlist">Allowlist, may ONLY combine with selected</SelectItem>
                      <SelectItem value="denylist">Denylist, may NOT combine with selected</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {form.combine_mode !== "any" && (
                  <div>
                    <Label className="eyebrow">
                      {form.combine_mode === "allowlist" ? "Allowed categories" : "Forbidden categories"}
                    </Label>
                    <div className="flex flex-wrap gap-1.5 mt-2" data-testid="rule-combine-cats">
                      {otherCategories.length === 0
                        ? <p className="text-xs text-[#4B5773]">No other categories.</p>
                        : otherCategories.map((c) => {
                          const on = (form.combine_category_ids || []).includes(c.category_id);
                          return (
                            <button type="button" key={c.category_id}
                              data-testid={`rule-combine-cat-${c.category_id}`}
                              onClick={() => toggleCombineCat(c.category_id)}
                              className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                                on
                                  ? (form.combine_mode === "allowlist"
                                      ? "bg-emerald-100 text-emerald-700 border-emerald-300"
                                      : "bg-red-100 text-red-700 border-red-300")
                                  : "bg-white border-slate-200 text-[#4B5773] hover:bg-slate-50"
                              }`}>{c.name}</button>
                          );
                        })}
                    </div>
                  </div>
                )}

                <div>
                  <Label className="eyebrow">Maximum tasks per employee at once (optional)</Label>
                  <Input type="number" min="1" data-testid="rule-max-concurrent"
                    value={form.max_concurrent_for_employee ?? ""}
                    onChange={(e) => set({
                      max_concurrent_for_employee:
                        e.target.value === "" ? null : Number(e.target.value),
                    })}
                    className="h-11 rounded-lg mt-2"
                    placeholder="No limit" />
                </div>
              </div>
            </TabsContent>

            <TabsContent value="eligibility">
              <div className="space-y-5">
                <SkillRows
                  title="Required skills"
                  testid="rule-required"
                  rows={form.required_skills}
                  skills={skills}
                  onAdd={addRequired}
                  onRemove={removeRequired}
                  renderRow={(row, i) => (
                    <>
                      <div className="col-span-5">
                        <SkillSelect
                          value={row.skill_id}
                          onChange={(v) => updateRequired(i, { skill_id: v })}
                          skills={skills}
                          testid={`rule-required-skill-${i}`} />
                      </div>
                      <div className="col-span-4">
                        <Select value={row.min_proficiency}
                          onValueChange={(v) => updateRequired(i, { min_proficiency: v })}>
                          <SelectTrigger className="h-9 rounded-lg"
                            data-testid={`rule-required-prof-${i}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {PROFICIENCY.map((p) => (
                              <SelectItem key={p} value={p}>Min: {PROFICIENCY_LABEL[p]}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <label className="col-span-3 flex items-center gap-1.5 text-[11px] text-[#4B5773]">
                        <input type="checkbox" className="accent-[#2563EB]"
                          checked={row.require_valid_cert}
                          data-testid={`rule-required-cert-${i}`}
                          onChange={(e) => updateRequired(i, { require_valid_cert: e.target.checked })} />
                        Valid cert
                      </label>
                    </>
                  )}
                />
                <SkillRows
                  title="Preferred skills (nice to have)"
                  testid="rule-preferred"
                  rows={form.preferred_skills}
                  skills={skills}
                  onAdd={addPreferred}
                  onRemove={removePreferred}
                  renderRow={(row, i) => (
                    <>
                      <div className="col-span-8">
                        <SkillSelect
                          value={row.skill_id}
                          onChange={(v) => updatePreferred(i, { skill_id: v })}
                          skills={skills}
                          testid={`rule-preferred-skill-${i}`} />
                      </div>
                      <div className="col-span-4">
                        <Input type="number" min="1" max="5"
                          data-testid={`rule-preferred-weight-${i}`}
                          value={row.weight}
                          onChange={(e) => updatePreferred(i, { weight: Number(e.target.value) || 1 })}
                          className="h-9 rounded-lg" placeholder="Weight 1-5" />
                      </div>
                    </>
                  )}
                />
              </div>
            </TabsContent>
          </Tabs>

          <DialogFooter className="mt-4">
            <Button type="submit" disabled={busy} data-testid="rule-submit"
              className="rounded-lg bg-[#2563EB] hover:bg-[#1D4ED8]">
              {busy ? "Saving…" : editing ? "Save changes" : "Create rule"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function SkillRows({ title, rows, onAdd, onRemove, renderRow, testid }) {
  return (
    <div className="border border-slate-200 rounded-lg p-3">
      <div className="flex items-center justify-between mb-2">
        <div className="eyebrow">{title}</div>
        <Button type="button" onClick={onAdd} size="sm"
          data-testid={`${testid}-add`}
          className="h-8 rounded-md bg-[#2563EB] hover:bg-[#1D4ED8] text-xs">
          <Plus className="h-3.5 w-3.5 mr-1" />Add
        </Button>
      </div>
      {rows.length === 0 ? (
        <p className="text-xs text-[#4B5773]" data-testid={`${testid}-empty`}>None.</p>
      ) : (
        <ul className="space-y-2" data-testid={`${testid}-list`}>
          {rows.map((r, i) => (
            <li key={i} className="grid grid-cols-12 gap-2 items-center"
                data-testid={`${testid}-row-${i}`}>
              {renderRow(r, i)}
              <Button type="button" variant="ghost" size="sm"
                className="col-span-12 lg:col-span-0 justify-self-end h-8 w-8 p-0"
                data-testid={`${testid}-remove-${i}`}
                onClick={() => onRemove(i)}>
                <Trash2 className="h-4 w-4 text-red-600" />
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function SkillSelect({ value, onChange, skills, testid }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="h-9 rounded-lg" data-testid={testid}>
        <SelectValue placeholder="Choose a skill" />
      </SelectTrigger>
      <SelectContent className="max-h-72">
        {skills.length === 0 && (
          <div className="px-3 py-2 text-xs text-[#4B5773]">No skills in the library.</div>
        )}
        {skills.map((s) => (
          <SelectItem key={s.skill_id} value={s.skill_id}>
            {s.name}
            <span className="text-[10px] text-[#4B5773] ml-2">
              {(s.kind || "additional") === "assignment" ? "Assignment" : "Additional"}
            </span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
